// ワードクラウド(customs/wordcloud.js)の PNG 保存まわりの純粋関数。
// wordcloud.js は wordcloud2 と DOM に依存して node --test から読めないため、ここに切り出している。

// 資料に貼る用途のため、テーマ(ダークモード)に関わらず白で塗る
export const EXPORT_BACKGROUND = '#ffffff'
// 書き出しは画面の devicePixelRatio に依らず、論理幅 800px を 2 倍(幅 1600px 相当)で描き直す
export const EXPORT_LOGICAL_WIDTH = 800
export const EXPORT_SCALE = 2
// 縦横比は画面上の枠に合わせるが、極端に縦長・横長にならないよう丸める
const MIN_ASPECT = 0.5
const MAX_ASPECT = 1
// 語が描かれた範囲の外側に残す余白(論理 px)
export const EXPORT_PADDING = 24

// ファイル名の 1 要素の長さ上限と整形は apex_chart_controller の CSV 名(sanitizeCsvFilenamePart)と同じ規則にする
const FILENAME_PART_MAX_LENGTH = 80

export const sanitizeFilenamePart = (value) => {
  if (value === null || value === undefined) return ''

  const sanitized = String(value)
    .trim()
    .replace(/\s*\/\s*/g, '・')
    .replace(/\s+/g, '')
    .replace(/[\\/:*?"<>|,]/g, '')
    .replace(/[. ]+$/g, '')

  const characters = Array.from(sanitized)
  if (characters.length <= FILENAME_PART_MAX_LENGTH) return sanitized

  return `${characters.slice(0, FILENAME_PART_MAX_LENGTH - 1).join('')}…`
}

// チャートの CSV 名と同じ並び「{対象名}_{識別子}_{SNS}_{チャート名}_{開始日-終了日}.png」で組み立てる。
// 対象名・識別子・SNS は data-chart-export-* から、期間は YYYYMMDD で受け取る。取れなかった要素は省く
export const buildWordcloudFilename = ({ entity, identifier, platform, chart = 'ワードクラウド', focusLabel, from, to } = {}) => {
  const chartName = focusLabel ? `${chart}(${focusLabel})` : chart
  const parts = [entity, identifier, platform, chartName]
    .map(sanitizeFilenamePart)
    .filter((part) => part)

  if (from && to) {
    parts.push(`${from}-${to}`)
  }

  return `${parts.join('_') || 'wordcloud'}.png`
}

// 書き出し用 canvas の寸法と描画設定。画面上の枠(width x height、CSS px)の縦横比だけを引き継ぐ。
// wordcloud2 は ceil(幅/gridSize) 列のグリッドを作るため、論理寸法は gridSize の倍数に切り下げる(画面側と同じ理由)
export const exportLayout = ({ width, height } = {}) => {
  const aspect = width > 0 && height > 0 ? Math.min(Math.max(height / width, MIN_ASPECT), MAX_ASPECT) : 0.75
  const gridSize = 30 // 画面側で幅 700px 以上のときと同じ間隔
  const logicalWidth = Math.floor(EXPORT_LOGICAL_WIDTH / gridSize) * gridSize
  const logicalHeight = Math.floor((EXPORT_LOGICAL_WIDTH * aspect) / gridSize) * gridSize

  return {
    gridSize,
    scale: EXPORT_SCALE,
    logicalWidth,
    logicalHeight,
    canvasWidth: logicalWidth * EXPORT_SCALE,
    canvasHeight: logicalHeight * EXPORT_SCALE
  }
}

// RGBA の画素配列から、不透明な画素がある範囲(両端を含む)を返す。何も描かれていなければ null
export const contentBounds = (data, width, height) => {
  let left = width
  let top = height
  let right = -1
  let bottom = -1

  for (let y = 0; y < height; y++) {
    const rowOffset = y * width * 4
    for (let x = 0; x < width; x++) {
      if (data[rowOffset + x * 4 + 3] === 0) continue

      if (x < left) left = x
      if (x > right) right = x
      if (y < top) top = y
      if (y > bottom) bottom = y
    }
  }

  return right < 0 ? null : { left, top, right, bottom }
}

// 書き出し画像の配置。描かれた範囲(範囲が無ければ canvas 全体)を切り出し(sx, sy, sw, sh)、
// 上下左右に padding の余白を足した大きさ(width, height)の画像の (padding, padding) に置く。
// 語が canvas の端まで描かれていても余白が残るよう、余白は canvas の端で打ち切らずに外側へ足す
export const exportFrame = (bounds, { padding = 0, width, height }) => {
  const source = bounds
    ? { sx: bounds.left, sy: bounds.top, sw: bounds.right - bounds.left + 1, sh: bounds.bottom - bounds.top + 1 }
    : { sx: 0, sy: 0, sw: width, sh: height }

  return { ...source, dx: padding, dy: padding, width: source.sw + padding * 2, height: source.sh + padding * 2 }
}
